import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { moduleOf, shellQuery } from '../../api/shell'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { CriticalityPill, failed } from '../parts'
import type { MapFinding, Severity } from '../map/mapApi'
import { ReportSchedulePanel } from './ReportSchedulePanel'
import {
  downloadMonthlyReportPdf,
  monthlyReportQuery,
  type Availability,
  type MonthlyReportPage as ReportData,
  type ReportApp,
} from './reportApi'

/**
 * Overview > Applications > Monthly report - raportul lunar PENTRU CLIENT.
 *
 * Pe fiecare aplicatie, pentru luna aleasa: disponibilitatea ("n/a" fara
 * date), tichetele pe componente (alarmele separat), cazurile la furnizor cu
 * timpul de raspuns fata de contract si depasirile, plus constatarile de risc
 * ACTUALE ale hartii. Totul il socoteste serverul (`MonthlyReport`); pagina il
 * aseaza. Print / Download PDF ca la harta. Perioada e o luna sau o
 * saptamana (luni-duminica). "Automatic sending" (`ReportSchedulePanel`) tine
 * programul, destinatarii si intrerupatorul firmei (stins implicit).
 */
const STYLES = ['css/adm.css', 'css/business_apps_map.css', 'css/business_apps_report.css']

const SEVERITY_LABEL: Record<Severity, string> = { high: 'High', medium: 'Medium', low: 'Low' }

function initial(name: string): string {
  return new URLSearchParams(window.location.search).get(name) ?? ''
}

function setParam(name: string, value: string) {
  const url = new URL(window.location.href)
  if (value !== '' && value !== '0') url.searchParams.set(name, value)
  else url.searchParams.delete(name)
  window.history.replaceState(null, '', url.toString())
}

function pct(value: number): string {
  return `${Number(value.toFixed(2))}%`
}

function minutes(m: number): string {
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`
}

export function MonthlyReportPage() {
  const shell = useQuery(shellQuery(moduleOf('/applications')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const [company, setCompany] = useState<number>(() => Math.max(0, Number(initial('company')) || 0))
  const [month, setMonth] = useState<string>(() => (/^\d{4}-(\d{2}|W\d{2})$/.test(initial('month')) ? initial('month') : ''))
  const [busy, setBusy] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const page = useQuery(monthlyReportQuery(company, month))

  if (isForbidden(page.error)) return <Forbidden />
  if (page.error) {
    return (
      <Content>
        <div className="alert alert-danger" role="alert" style={{ margin: 20 }}>
          Something went wrong: {page.error.message}
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data
  const companies = data.companies ?? []
  const hasReport = data.ready && !data.needsCompany

  const pdf = () => {
    setBusy(true)
    downloadMonthlyReportPdf(data.companyId, data.month)
      .catch((error) => toast(failed(error), 'Monthly report', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Content>
      <div className="am-toolbar am-noprint">
        <a href="/app/applications" className="btn btn-default">
          <i className="fas fa-arrow-left"></i> Applications
        </a>
        {companies.length > 0 && (
          <label className="am-company">
            Customer{' '}
            <select
              id="mrCompany"
              className="form-control input-sm"
              value={data.companyId}
              onChange={(e) => {
                setCompany(Number(e.target.value))
                setParam('company', e.target.value)
              }}
            >
              <option value={0}>- choose -</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="am-company">
          Period{' '}
          <select
            id="mrMonth"
            className="form-control input-sm"
            value={data.month}
            onChange={(e) => {
              setMonth(e.target.value)
              setParam('month', e.target.value)
            }}
          >
            {(data.weeks ?? []).length > 0 ? (
              <>
                <optgroup label="Months">
                  {data.months.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Weeks (Monday to Sunday)">
                  {(data.weeks ?? []).map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              </>
            ) : (
              data.months.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))
            )}
          </select>
        </label>
        {hasReport && (
          <>
            <span className="am-spacer"></span>
            {data.canEdit && (
              <button type="button" className="btn btn-default" id="mrSettingsToggle" onClick={() => setShowSettings((v) => !v)}>
                <i className="fas fa-envelope"></i> Automatic sending
              </button>
            )}
            <button type="button" className="btn btn-default" id="mrPrint" onClick={() => window.print()}>
              <i className="fas fa-print"></i> Print
            </button>
            {data.pdfAvailable && (
              <button type="button" className="btn btn-primary" id="mrPdf" disabled={busy} onClick={pdf}>
                <i className="fas fa-file-pdf"></i> Download PDF
              </button>
            )}
          </>
        )}
      </div>

      {hasReport && showSettings && <ReportSchedulePanel company={data.companyId} />}

      {!data.ready ? (
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <i className="fas fa-database"></i> {data.message ?? 'Business applications are not set up on this server yet.'}
        </div>
      ) : data.needsCompany ? (
        <div className="alert alert-info" style={{ margin: 20 }} id="mrNeedsCompany">
          <i className="fas fa-building"></i> {data.message ?? 'Choose the customer whose monthly report you want to see.'}
        </div>
      ) : (
        <ReportDocument page={data} />
      )}
    </Content>
  )
}

function ReportDocument({ page }: { page: ReportData }) {
  const apps = page.apps ?? []
  const findings = page.findings ?? []
  const s = page.summary

  return (
    <div id="appReport" className="am-doc">
      <header className="am-head">
        <div>
          <h1>
            Applications report - {page.monthLabel}
            {page.monthToDate ? ' (to date)' : ''}
          </h1>
          <div className="mr-company">{page.companyName}</div>
          {page.periodRange ? <div className="am-muted" id="mrRange">Period: {page.periodRange}</div> : null}
          <div className="am-muted">Prepared {page.generatedAt}</div>
        </div>
      </header>

      {s && (
        <section className="am-summary" aria-label="Summary">
          <div className="am-tiles mr-tiles">
            <Tile value={String(s.apps)} label="applications" sub={`${s.critical} critical`} />
            <Tile
              value={s.availability === null ? 'n/a' : pct(s.availability)}
              label="average availability"
              sub={s.measured > 0 ? `${s.measured} of ${s.apps} measured` : 'no availability data'}
            />
            <Tile value={String(s.tickets)} label="tickets" sub={`${s.alarms} from alarms · ${s.openTickets} open`} />
            <Tile value={String(s.cases)} label="vendor cases" sub={`${s.breaches} late`} tone={s.breaches > 0 ? 'high' : undefined} />
            <Tile value={String(s.findings)} label="risk findings" sub={`${s.high} high`} tone={s.high > 0 ? 'high' : undefined} />
          </div>
        </section>
      )}

      <section className="am-section" aria-label="Overview">
        <h2>Overview</h2>
        {apps.length === 0 ? (
          <p className="am-muted">No business application is recorded for this customer.</p>
        ) : (
          <table className="table mr-table" id="mrOverview">
            <thead>
              <tr>
                <th>Application</th>
                <th>Criticality</th>
                <th>Availability</th>
                <th>Tickets (alarms)</th>
                <th>Vendor cases</th>
                <th>Late responses</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id}>
                  <td>
                    <a href={`#mr-app-${a.id}`}>{a.name}</a>
                  </td>
                  <td>
                    <CriticalityPill value={a.criticality} label={a.criticalityLabel} />
                  </td>
                  <td>
                    <AvailabilityText value={a.availability} />
                  </td>
                  <td>
                    {a.tickets.total} ({a.tickets.alarms})
                  </td>
                  <td>
                    {a.cases.total}
                    {a.cases.open > 0 ? `, ${a.cases.open} open` : ''}
                  </td>
                  <td className={a.cases.breaches > 0 ? 'mr-bad' : undefined}>{a.cases.breaches}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {apps.map((a) => (
        <AppSection key={a.id} app={a} />
      ))}

      <section className="am-section" aria-label="Risk findings">
        <h2>Current risk findings</h2>
        <p className="am-muted am-lead">As of {page.generatedAt}, from the application map - not a history of the month.</p>
        {findings.length === 0 ? (
          <p className="am-muted">No risk found in the data we hold.</p>
        ) : (
          <ol className="am-findings" id="mrFindings">
            {findings.map((f, i) => (
              <Finding key={`${f.rule}-${i}`} finding={f} />
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}

function AppSection({ app }: { app: ReportApp }) {
  const t = app.tickets
  const c = app.cases
  const support = [
    app.supportVendor || 'not recorded',
    app.contractNo ? `contract ${app.contractNo}` : '',
    app.responseHours !== null ? `response within ${app.responseHours}h` : '',
  ]
    .filter((x) => x !== '')
    .join(', ')

  return (
    <section className="am-section mr-app" id={`mr-app-${app.id}`} aria-label={app.name}>
      <h2>{app.name}</h2>
      <div className="mr-app-meta">
        <CriticalityPill value={app.criticality} label={app.criticalityLabel} /> Availability: <AvailabilityText value={app.availability} /> · Support:{' '}
        {support}
      </div>

      <h3 className="mr-h3">Tickets on its components</h3>
      <p className="mr-line">
        {t.total} ({t.alarms} alarms), {t.resolved} resolved, {t.open} still open.
      </p>
      {t.list.length > 0 && (
        <table className="table mr-table mr-tickets">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Subject</th>
              <th>Opened</th>
              <th>Resolved</th>
            </tr>
          </thead>
          <tbody>
            {t.list.map((x) => (
              <tr key={x.id}>
                <td>#{x.id}</td>
                <td>
                  {x.alarm && <span className="am-tag am-tag-warn">alarm</span>} {x.subject}
                </td>
                <td>{x.created}</td>
                <td>{x.resolved ?? <span className="am-muted">open</span>}</td>
              </tr>
            ))}
            {t.total > t.list.length && (
              <tr>
                <td colSpan={4} className="am-muted">
                  and {t.total - t.list.length} more
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <h3 className="mr-h3">Vendor cases</h3>
      <p className="mr-line">
        {c.total} ({c.opened} new, {c.closed} closed, {c.open} open); average first response{' '}
        {c.avgResponseHours === null ? 'n/a' : `${c.avgResponseHours}h`} (
        {c.contractHours === null ? 'no response time in the contract' : `contract: ${c.contractHours}h`})
        {c.breaches > 0 && <span className="mr-bad">; {c.breaches} past the deadline</span>}.
      </p>
      {c.list.length > 0 && (
        <table className="table mr-table mr-cases">
          <thead>
            <tr>
              <th>Case</th>
              <th>Status</th>
              <th>Opened</th>
              <th>Deadline</th>
              <th>First response</th>
              <th>Closed</th>
            </tr>
          </thead>
          <tbody>
            {c.list.map((x) => (
              <tr key={x.id} className={x.late || x.overdue ? 'mr-row-bad' : undefined}>
                <td>{x.ref !== '' ? x.ref : `ticket #${x.ticketId}`}</td>
                <td>{x.statusLabel}</td>
                <td>{x.openedAt}</td>
                <td>{x.dueAt ?? '-'}</td>
                <td>
                  {x.respondedAt === null ? (
                    x.overdue ? (
                      <span className="mr-bad">none, overdue</span>
                    ) : (
                      <span className="am-muted">waiting</span>
                    )
                  ) : (
                    <>
                      {x.respondedAt}
                      {x.late && <span className="mr-bad"> late</span>}
                    </>
                  )}
                </td>
                <td>{x.closedAt ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function AvailabilityText({ value }: { value: Availability }) {
  if (value === null) return <span className="am-muted">n/a</span>
  return (
    <span>
      {pct(value.pct)}
      {value.downMinutes !== null && value.downMinutes > 0 && <span className="am-muted"> ({minutes(value.downMinutes)} down)</span>}
    </span>
  )
}

function Tile({ value, label, sub, tone }: { value: string; label: string; sub?: string; tone?: Severity }) {
  return (
    <div className={`am-tile${tone ? ` am-tile-${tone}` : ''}`}>
      <div className="am-tile-value">{value}</div>
      <div className="am-tile-label">{label}</div>
      {sub && <div className="am-tile-sub">{sub}</div>}
    </div>
  )
}

function Finding({ finding }: { finding: MapFinding }) {
  return (
    <li className={`am-finding am-sev-${finding.severity}`}>
      <span className={`am-sev am-sev-badge-${finding.severity}`}>{SEVERITY_LABEL[finding.severity]}</span>
      <div className="am-finding-body">
        <div className="am-finding-title">{finding.title}</div>
        <div className="am-finding-detail">{finding.detail}</div>
        <div className="am-finding-rec">
          <i className="fas fa-arrow-right"></i> {finding.recommendation}
        </div>
      </div>
    </li>
  )
}
